import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLazyQuoteApi, useQuoteApi } from 'Shared/services/Api';
import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import { env } from 'Shared/constants/env';

export type DetailsProviderType = {
  loading: boolean;
  details: GetDetailsResponseType | null;
  refreshDetails(): Promise<GetDetailsResponseType>;
  setDetails(newDetails: GetDetailsResponseType): void;
  acknowledgeQuoteChange(): void;
  shouldInitializeCoverages: boolean;
  initializeCoveragesLoading: boolean;
  initializeCoverages(): Promise<GetDetailsResponseType>;
};

export const useDetails = (
  appId: string,
  isUnauthorized: boolean,
  handleUnauthorizedAgent: (e: any) => boolean
): DetailsProviderType => {
  const [details, changeDetails] = useState<GetDetailsResponseType | null>(
    null
  );

  const [{ data, error, loading, refetch: fetchDetails }] = useQuoteApi<
    GetDetailsResponseType
  >(
    {
      url: `/insurance_applications/${appId}/details`,
      transformResponse: transformAppDetails,
    },
    false,
    false // do not throw error on first load, so we can handle it below
  );

  const [
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _api,
    acknowledgeQuoteChangeRequest,
  ] = useLazyQuoteApi<null>({
    url: `/insurance_applications/${appId}/acknowledge_quote_change`,
    method: 'POST',
  });

  const acknowledgeQuoteChange = useCallback(() => {
    acknowledgeQuoteChangeRequest();
    // Optimistically acknowledge and hide info alert.
    changeDetails(
      oldDetails =>
        ({
          ...oldDetails,
          quoteChangeAcknowledgement: false,
        } as GetDetailsResponseType)
    );
  }, [acknowledgeQuoteChangeRequest]);

  const initializeCoveragesCommand = useMemo(() => {
    return details?.commands?.insuranceProduct?.find(
      c => c.name === 'initialize_coverages'
    );
  }, [details]);

  const [
    { loading: initializeCoveragesLoading },
    initializeCoveragesRequest,
  ] = useLazyQuoteApi<GetDetailsResponseType>({
    url: initializeCoveragesCommand?.uri,
    baseURL: env.reznorHostname, // command uri already starts with '/api/v3'
    method: 'POST',
    transformResponse: transformAppDetails,
  });

  const shouldInitializeCoverages = !!initializeCoveragesCommand?.runnable;

  const initializeCoverages = useCallback(() => {
    return initializeCoveragesRequest().then(response => {
      changeDetails(response.data);
      return response.data;
    });
  }, [initializeCoveragesRequest]);

  const setDetails = useCallback((newDetails: GetDetailsResponseType) => {
    changeDetails(newDetails);
  }, []);

  const refreshDetails = useCallback(async (): Promise<
    GetDetailsResponseType
  > => {
    return fetchDetails({
      url: `/insurance_applications/${appId}/details`,
      transformResponse: transformAppDetails,
    })
      .then(response => {
        changeDetails(response.data);
        return response.data;
      })
      .catch(e => {
        handleUnauthorizedAgent(e);
        throw e;
      });
  }, [appId, fetchDetails, handleUnauthorizedAgent]);

  useEffect(() => {
    if (data && !details) {
      setDetails(data);
    }
  }, [data, details, setDetails]);

  useEffect(() => {
    if (error && !isUnauthorized) {
      const agentUnauthorized = handleUnauthorizedAgent(error);
      if (agentUnauthorized) {
        // allows the app to route to the correct page
        setDetails({} as GetDetailsResponseType);
      } else {
        // otherwise this error will be caught by the error boundary
        throw error;
      }
    }
  }, [error, handleUnauthorizedAgent, isUnauthorized, setDetails]);

  return {
    loading,
    details,
    refreshDetails,
    setDetails,
    acknowledgeQuoteChange,
    shouldInitializeCoverages,
    initializeCoveragesLoading,
    initializeCoverages,
  };
};
